import React, { useContext, useMemo } from "react";
import { Accordion, Button } from "react-bootstrap";
import { Radio, Typography } from "antd";
import { ShoppingCartOutlined, ProductOutlined, ApiOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import ImageCard from "./ImageCard";
import { CustomerProps } from "../../ShowCustomer";
import { useSmartAssistStore } from "../store";
import { NAV_ITEMS, SECTIONS } from "../helper";
import { get, map, filter, split, trim } from "lodash";
import { ActiveEngagement } from "../../ShowEngagement";

const { Title } = Typography;

const ICONS_MAP = {
  [SECTIONS.PRODUCT_USER_KIT]: <ShoppingCartOutlined style={{ color: "#1890af" }} className="me-2" />,
  [SECTIONS.ALL_PRODUCTS]: <ProductOutlined style={{ color: "#1890af" }} className="me-2" />,
  [SECTIONS.COMBINED_USAGE]: <ApiOutlined style={{ color: "#1890af" }} className="me-2" />,
  [SECTIONS.GENERAL_QUERIES]: <QuestionCircleOutlined style={{ color: "#1890af" }} className="me-2" />,
};

const SmartAssistPhase2 = () => {
  const {
    saPastResponse,
    saCustomerIssues,
    saSubmitIssue,
    saPastResolution,
    resetSaEvaluatedResolution,
    setCurrentSaPhase,
    setSelectedIssue,
    setResponses,
    isCxUsedProductBefore,
    setIsCxUsedProductBefore,
    setIsSubmitDisabled
  } = useSmartAssistStore();

  const { customer } = useContext(CustomerProps);
  const { activeEngagement } = useContext(ActiveEngagement);

  const howToUse = saCustomerIssues.find((item) => item.label === NAV_ITEMS.HOW_TO_USE);

  const onClickContinue = async (product) => {
    setResponses({});
    setCurrentSaPhase(NAV_ITEMS.SUMMARY);
    setSelectedIssue(product);
    const questionsMap = await saSubmitIssue(customer.id, product.issue_key, activeEngagement);
    saPastResponse(customer.id, { question_ids: Object.keys(questionsMap || {}).map(Number) });
    saPastResolution(customer.id, product.issue_key);
    resetSaEvaluatedResolution();
    setIsSubmitDisabled(true);
  };

  const sortedKits = useMemo(
    () =>
      howToUse?.sub?.map((kit) => ({
        ...kit,
        sub: kit.sub?.sort((a, b) => a.label.localeCompare(b.label)),
      })),
    [howToUse]
  );

  return (
    <div>
      <div className="d-flex justify-content-between">
        <Title level={4} style={{ color: "#1890ff" }}>
          Select the product to know more...
        </Title>
        <span>
          <span className="me-2">Has the customer used any products before?</span>
          <Radio.Group autoFocus size="small" value={isCxUsedProductBefore} onChange={e => setIsCxUsedProductBefore(e.target.value)}>
            <Radio.Button value={true}>Yes</Radio.Button>
            <Radio.Button value={false}>No</Radio.Button>
          </Radio.Group>
        </span>
      </div>

      <Accordion defaultActiveKey={'0'}>
        {sortedKits?.map((kit, kitIndex) => {

          const shouldShowAccordion =
            ![SECTIONS.COMBINED_USAGE, SECTIONS.GENERAL_QUERIES].includes(kit.label) ||
            !!kit.product_read;

          return (
            shouldShowAccordion && (
              <Accordion.Item eventKey={kitIndex.toString()} key={kitIndex}>
                <Accordion.Header>
                  <KitTitle label={kit.label} />
                </Accordion.Header>

                <Accordion.Body>
                  {kit.sub?.length > 0 && (
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                      {kit.sub.map((product, productIndex) => (
                        <ProductCard
                          key={productIndex}
                          product={product}
                          onClickContinue={onClickContinue}
                        />
                      ))}
                    </div>
                  )}
                  <ProductRead productRead={kit.product_read} />
                </Accordion.Body>
              </Accordion.Item>
            )
          );
        })}
      </Accordion>

    </div>
  );
};

const KitTitle = ({ label }) => {
  const icon = ICONS_MAP[label] || null;
  return (
    <span className="d-flex align-items-center">
      {icon} {label}
    </span>
  );
};

const ProductCard = ({ product, onClickContinue }) => (
  <div style={{ flex: "0 0 calc(25% - 15px)" }} className="d-flex align-items-center flex-column">
    <ImageCard product={product} />
    <Button
      className="custom-hover-button rounded-3 px-4 py-2"
      onClick={() => onClickContinue(product)}
      style={{ fontSize: "11px" }}
    >
      Select Product
    </Button>
  </div>
);

const ProductRead = ({ productRead }) => {
  if (!productRead) return null;

  return (
    <div className="card mb-4">
      <div className="card-body">
        <Accordion>
          {productRead.map((read, index) => (
            <Accordion.Item eventKey={index.toString()} key={read.id || index}>
              <Accordion.Header>{read.heading || `Panel ${index + 1}`}</Accordion.Header>
              <Accordion.Body>
                {get(read, "content") ? (
                  map(
                    filter(split(read.content, "\n"), (line) => trim(line).length > 0),
                    (line, i) => <p key={i}>{line}</p>
                  )
                ) : (
                  <p className="text-muted">No content provided.</p>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

export default SmartAssistPhase2;
